import firebase from 'firebase/app';
import { useMemo, useState } from 'react';
import useSWR from 'swr';

import { useAuthContext } from '../../auth';
import {
    archiveSupportPost,
    createSupportPost,
    getSupportPosts,
    SupportPostData,
    unarchiveSupportPost,
    updateSupportPost,
} from '../../common/api/supportPostsApi';

export type SupportPost = {
    id: string;
    tag?: string;
    shortDescription?: string;
    description?: string;
    date?: Date;
    isArchived?: boolean;
};

// TODO: refactor it, move to separate file
export type UseAsyncMutation<P extends any[]> = (
    props?: UseAsyncMutationProps,
) => {
    isLoading: boolean;
    error: unknown | null;
    mutate: (...params: P) => Promise<void>;
};

export type UseAsyncMutationProps = {
    onSuccess?: () => void;
    onFailure?: (error: unknown) => void;
    onFinally?: () => void;
    onLoading?: () => void;
};

export const createUseAsyncMutation = <P extends any[], R>(
    fn: (firebaseUser: firebase.User, ...params: P) => Promise<R>,
): UseAsyncMutation<P> => (props) => {
    const { onLoading, onSuccess, onFailure, onFinally } = props || {};
    const { firebaseUser } = useAuthContext();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<unknown | null>(null);

    const mutate = async (...params: P) => {
        if (!firebaseUser) {
            throw new Error('User not logged in');
        }
        onLoading?.();
        setIsLoading(true);
        setError(null);
        try {
            await fn(firebaseUser, ...params);
            onSuccess?.();
        } catch (e: any) {
            setError(e);
            onFailure?.(e);
        } finally {
            setIsLoading(false);
            onFinally?.();
        }
    };

    return { isLoading, error, mutate };
};

export type UseSupportPostsProps = {
    includeArchived?: boolean;
    selectedTags?: string[];
};

export const useSupportPosts = (props?: UseSupportPostsProps) => {
    const { includeArchived, selectedTags } = props || {};
    const { firebaseUser } = useAuthContext();
    const { data, isValidating, mutate } = useSWR(
        ['support_posts', includeArchived, selectedTags],
        async ([, includeArchived]) => {
            if (!firebaseUser) {
                return [];
            }
            return getSupportPosts(firebaseUser, {
                includeArchived,
                selectedTags,
            });
        },
        {
            compare: (a, b) => JSON.stringify(a) === JSON.stringify(b),
            revalidateOnFocus: false,
        },
    );

    const refresh = async () => {
        await mutate();
    };

    const supportPosts: SupportPost[] = useMemo(() => {
        if (!data) {
            return [];
        }
        return data.map(mapSupportPostToModel);
    }, [data]);

    return { supportPosts, isLoading: isValidating, refresh };
};

export const useSupportCreatePost = createUseAsyncMutation(createSupportPost);
export const useSupportUpdatePost = createUseAsyncMutation(updateSupportPost);
export const useSupportArchivePost = createUseAsyncMutation(archiveSupportPost);
export const useSupportUnarchivePost = createUseAsyncMutation(unarchiveSupportPost);

const mapSupportPostToModel = (article: SupportPostData): SupportPost => ({
    id: article.id,
    tag: article.tag,
    shortDescription: article.shortDescription,
    description: article.description,
    date: article.publishedAt ? new Date(article.publishedAt) : undefined,
    isArchived: !!article.archivedAt,
});
